import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import { TaskAttachment } from "../../types";
import {
  downloadAttachmentFile,
  formatFileSize,
  isImageAttachment,
  isPdfAttachment,
} from "../../lib/storage";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { getAttachmentIcon } from "../forms/attachmentIcon";
import { indexAfterRemove, wrapIndex } from "../../lib/captureReview";

export interface AttachmentViewerProps {
  attachments: TaskAttachment[];
  /**
   * Which attachment to open on. Read once, at mount, so a caller that wants
   * to re-point an already-open viewer at a different attachment must remount
   * it (e.g. `key={viewerIndex}`) rather than change this prop in place.
   */
  initialIndex: number;
  onClose: () => void;
}

/**
 * In-app file viewer opened from `AttachmentList`. Replaces the browser's
 * own new-tab navigation so a user paging through a task's files doesn't
 * have to close each tab before opening the next one — ← / → step to the
 * previous/next attachment (wrapping at the ends), and the file strip
 * along the bottom tracks which one is current.
 *
 * Images and PDFs render inline; every other file type falls back to an
 * icon + name + download, since the browser has no built-in way to
 * preview them in an iframe.
 *
 * `attachments` is the caller's live array, not a snapshot — a delete from
 * `AttachmentList` underneath can shrink it while this stays open. `index`
 * is reconciled against that (by attachment id, falling back to
 * captureReview.ts's `indexAfterRemove`) rather than just clamped, so the
 * viewer keeps showing the same file when possible instead of landing on
 * whatever now occupies its old slot.
 *
 * Rendered through a portal to `document.body`: TaskDialog's own panel has
 * a persistent (if visually no-op) `transform` left behind by its entrance
 * animation's `animation-fill-mode: both`, and any `transform` on an
 * ancestor makes it the containing block for `position: fixed` descendants.
 * Left inline, this backdrop would size and position itself against that
 * panel instead of the real viewport. Same reason Tooltip portals its tip.
 */
const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  attachments,
  initialIndex,
  onClose,
}) => {
  const total = attachments.length;
  const [index, setIndex] = React.useState(() =>
    wrapIndex(initialIndex, total),
  );

  // Keep pointing at the same attachment (or the nearest remaining one) when
  // `attachments` changes size while the viewer stays open — e.g. a delete
  // from AttachmentList underneath, which doesn't unmount this viewer.
  // Mirrors CaptureReview's own "page through a list, an item can vanish"
  // handling, but reconciled DURING render rather than in an effect: an
  // effect only corrects `index` a render *after* the shrink, and this
  // component dereferences `attachments[index]` unconditionally below, so
  // that one intermediate render — with the old index against the new,
  // shorter array — would already have crashed before the effect ever runs.
  // Adjusting state while rendering (see React's docs on "storing
  // information from previous renders") re-renders with the fix applied
  // before anything is committed or painted, so there's no unsafe frame.
  const [prevAttachments, setPrevAttachments] = React.useState(attachments);
  let effectiveIndex = index;
  if (attachments !== prevAttachments) {
    setPrevAttachments(attachments);
    if (total > 0) {
      const viewedId = prevAttachments[index]?.id;
      const stillAt = attachments.findIndex((a) => a.id === viewedId);
      effectiveIndex = stillAt >= 0 ? stillAt : indexAfterRemove(index, total);
      if (effectiveIndex !== index) setIndex(effectiveIndex);
    }
  }

  // Defensive: the viewer never offers a delete action itself, but if the
  // list it was opened from becomes empty out from under it, close rather
  // than render on an out-of-range attachment.
  useEffect(() => {
    if (total === 0) onClose();
  }, [total, onClose]);

  const goPrev = useCallback(() => {
    setIndex((i) => wrapIndex(i - 1, total));
  }, [total]);
  const goNext = useCallback(() => {
    setIndex((i) => wrapIndex(i + 1, total));
  }, [total]);

  useEscapeKey(onClose);

  const panelRef = useRef<HTMLDivElement>(null);
  useDialogFocus(panelRef);

  // Capture-phase window listener with stopImmediatePropagation, mirroring
  // SchedulePickerCalendar's nested-dialog convention, so ←/→ don't also
  // reach a board or calendar view mounted underneath this modal-on-modal.
  useEffect(() => {
    if (total <= 1) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopImmediatePropagation();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopImmediatePropagation();
        goNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [total, goPrev, goNext]);

  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, attachment: TaskAttachment) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return; // let the browser handle modifier/middle-clicks natively
      }
      e.preventDefault();
      downloadAttachmentFile(
        attachment.storage_url,
        attachment.file_name,
      ).catch(() => {
        window.open(attachment.storage_url, "_blank", "noopener,noreferrer");
      });
    },
    [],
  );

  if (total === 0) return null;
  const current = attachments[effectiveIndex];
  const IconComponent = getAttachmentIcon(current.mime_type);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-40 p-2 sm:p-6 animate-dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Viewing ${current.file_name}`}
        tabIndex={-1}
        className="bg-warm-bg-card rounded-card border border-warm-border shadow-soft-lg w-full max-w-4xl h-[85vh] flex flex-col animate-dialog-panel outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-warm-border">
          <IconComponent
            size={18}
            className="flex-shrink-0 text-warm-text-secondary"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-warm-text-primary truncate">
              {current.file_name}
            </p>
            <p
              className="text-xs text-warm-text-tertiary"
              aria-live="polite"
            >
              {formatFileSize(current.file_size)}
              {total > 1 && ` · ${effectiveIndex + 1} of ${total}`}
            </p>
          </div>
          <a
            href={current.storage_url}
            target="_blank"
            rel="noopener noreferrer"
            download={current.file_name}
            onClick={(e) => handleDownload(e, current)}
            aria-label="Download"
            className="p-1.5 text-warm-text-secondary hover:text-warm-blue transition-colors rounded hover:bg-warm-border-subtle"
          >
            <Download size={16} />
          </a>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 text-warm-text-secondary hover:text-warm-text-primary transition-colors rounded hover:bg-warm-border-subtle"
          >
            <X size={16} />
          </button>
        </div>

        {/* Preview */}
        <div className="relative flex-1 flex items-center justify-center overflow-hidden p-4 sm:p-6">
          {total > 1 && (
            <button
              onClick={goPrev}
              aria-label="Previous file"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-warm-bg-card/90 border border-warm-border shadow-soft-md text-warm-text-secondary hover:text-warm-blue transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          {isImageAttachment(current.mime_type) ? (
            <img
              src={current.storage_url}
              alt={current.file_name}
              className="max-h-full max-w-full object-contain rounded-soft"
            />
          ) : isPdfAttachment(current.mime_type) ? (
            // No `sandbox` here, unlike EmailBodyRenderer's iframe: Chrome's
            // built-in PDF viewer refuses to activate inside a sandboxed
            // iframe at all (confirmed — it falls back to a broken-file icon
            // regardless of which permissions the attribute grants), so
            // sandboxing would trade a working preview for a broken one.
            <iframe
              src={current.storage_url}
              title={current.file_name}
              className="w-full h-full rounded-soft border border-warm-border"
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-center max-w-xs">
              <IconComponent size={56} className="text-warm-text-tertiary" />
              <p className="text-sm text-warm-text-secondary">
                No preview available for this file type.
              </p>
              <a
                href={current.storage_url}
                target="_blank"
                rel="noopener noreferrer"
                download={current.file_name}
                onClick={(e) => handleDownload(e, current)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-soft text-sm transition-colors"
              >
                <Download size={14} />
                Download
              </a>
            </div>
          )}

          {total > 1 && (
            <button
              onClick={goNext}
              aria-label="Next file"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-warm-bg-card/90 border border-warm-border shadow-soft-md text-warm-text-secondary hover:text-warm-blue transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>

        {/* File strip — tracks and jumps to the current attachment */}
        {total > 1 && (
          <div className="flex items-center gap-2 px-4 py-3 border-t border-warm-border overflow-x-auto">
            {attachments.map((attachment, i) => {
              const isCurrent = i === effectiveIndex;
              const ThumbIcon = getAttachmentIcon(attachment.mime_type);
              return (
                <button
                  key={attachment.id}
                  onClick={() => setIndex(i)}
                  aria-label={`View ${attachment.file_name}`}
                  aria-current={isCurrent ? "true" : undefined}
                  className={`flex-shrink-0 w-10 h-10 rounded-soft border overflow-hidden flex items-center justify-center transition-colors ${
                    isCurrent
                      ? "border-warm-blue ring-2 ring-warm-blue bg-warm-blue/10"
                      : "border-warm-border bg-warm-bg-elevated hover:border-warm-blue/50"
                  }`}
                >
                  {isImageAttachment(attachment.mime_type) ? (
                    <img
                      src={attachment.storage_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ThumbIcon size={16} className="text-warm-text-secondary" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AttachmentViewer;
